<script setup lang="ts" generic="T extends string">
defineProps<{
  gridTemplateColumns: (
    | `${number}${'px' | 'fr'}`
    | 'auto'
    | `minmax(${number}px, ${'auto' | `${number}fr`})`
  )[]
  headerProps?: { [key: string]: unknown }
  isTable?: boolean
  hasCustomCellStyle?: boolean
}>()
</script>

<template>
  <!-- TODO: Refactor to use semantic elements `td`, `tr`, `th`, `table`, `caption`. See https://accessibility.education.gov.uk/guidelines/wcag/explorer#1-3-1a for details. -->
  <component
    :is="isTable ? 'table' : 'section'"
    :class="$style.table"
    :style="`grid-template-columns: ${gridTemplateColumns.join(' ')};`"
    :has-custom-cell-style
  >
    <span
      :class="$style['table-header']"
      v-bind="headerProps"
    >
      <slot name="header" />
    </span>
    <slot />
  </component>
</template>

<style module>
  .table {
    width: 100%; align-self: stretch; display: grid;
  }

  /* Table cells */
  .table:not([has-custom-cell-style=true]) > *:not(.table-header) {
    height: 48px;
    display: flex;
    align-items: center;
  }

  /* Table header — spans all columns so its border is continuous even with column gaps */
  .table-header {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
  }
  /* Table header cells */
  .table-header > * {
    min-height: 40px;
    display: flex;
    align-items: center;
    line-height: 1.3;
    color: color-mix(in oklab, currentcolor 50%, var(--border-color));
    font-weight: 900;
  }

  /*
   * Cells must occupy exactly one column each. Without this, system attributes like
   * `ui-inline-size` (meant for card-grid layouts, where a size also implies a column
   * span) leak their `grid-column: span N` into table rows, shifting every following
   * cell over. Declared before the auto-expand rules below so those still win (same
   * specificity) when they actually apply.
   */
  .table > *:not(.table-header) {
    grid-column: auto;
  }

  /* Auto-expand cells if following cells are empty */
  .table > :has(+ :empty) {
    grid-column-end: span 2;
  }
  .table > :has(+ :empty + :empty) {
    grid-column-end: span 3;
  }
  .table > :has(+ :empty + :empty + :empty) {
    grid-column-end: span 4;
  }

  /* Hide empty content (after the header row)  */
  .table > .table-header ~ :empty {
    display: none;
  }
</style>
